import { useEffect, useRef, useState } from "react";
import { FACTORY_INTRO_DATA as DATA } from "@/pages/factory/factoryIntroData";

/**
 * Full-screen logo-assembly intro (2800 ms) played when entering the Factory
 * workspace. Ported 1:1 from the supplied standalone canvas animation.
 * Fades out (450 ms) before handing over to the workspace.
 * Respects prefers-reduced-motion (finishes immediately, no fade).
 */
export function FactoryIntro({ onDone }) {
  const canvasRef = useRef(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const clamp = (x) => Math.max(0, Math.min(1, x));
    const smooth = (x) => {
      x = clamp(x);
      return x * x * x * (x * (x * 6 - 15) + 10);
    };
    const lerp = (a, b, t) => a + (b - a) * t;
    let frame = 0;
    let fadeTimer = 0;

    const finish = () => {
      setFading(true);
      fadeTimer = window.setTimeout(() => doneRef.current?.(), 450);
    };

    function resize() {
      canvas.width = Math.round(window.innerWidth * window.devicePixelRatio);
      canvas.height = Math.round(window.innerHeight * window.devicePixelRatio);
    }

    function path(loops, project) {
      ctx.beginPath();
      for (const loop of loops) {
        const pts = loop.map(project);
        if (!pts.length) continue;
        const a = pts[pts.length - 1];
        const b = pts[0];
        ctx.moveTo((a[0] + b[0]) / 2, (a[1] + b[1]) / 2);
        pts.forEach((p, j) => {
          const q = pts[(j + 1) % pts.length];
          ctx.quadraticCurveTo(p[0], p[1], (p[0] + q[0]) / 2, (p[1] + q[1]) / 2);
        });
        ctx.closePath();
      }
    }

    function letterPath(loops, project) {
      ctx.beginPath();
      for (const loop of loops) {
        loop.forEach((v, j) => {
          const p = project(v);
          if (j) ctx.lineTo(p[0], p[1]);
          else ctx.moveTo(p[0], p[1]);
        });
        ctx.closePath();
      }
    }

    function render(t) {
      const W = canvas.width;
      const H = canvas.height;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = "#0c1015";
      ctx.fillRect(0, 0, W, H);
      const sc = Math.min(W / 1280, H / 720);
      ctx.translate(W / 2, H / 2);
      ctx.scale(sc, sc);
      ctx.translate(-640, -360);
      const bg = ctx.createRadialGradient(610, 320, 10, 640, 360, 650);
      bg.addColorStop(0, "#222b35");
      bg.addColorStop(0.56, "#121920");
      bg.addColorStop(1, "#0c1015");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, 1280, 720);
      const lock = smooth((t - 1.62) / 0.69);
      const intro = smooth(t / 0.3);
      const morph = smooth((t - 1.95) / 0.43);
      const size = lerp(3.1, 1.85, lock);
      const originX = lerp(640, 405, lock);
      const originY = 355;
      const tiltX = lerp(0.68, 0, lock);
      const tiltY = lerp(-0.42, 0, lock);
      // Quiet production-table guides disappear as the mark locks.
      ctx.globalAlpha = (1 - lock) * 0.18;
      ctx.strokeStyle = "#8396a6";
      ctx.lineWidth = 0.7;
      for (let i = -3; i <= 3; i++) {
        ctx.beginPath();
        ctx.moveTo(330 + i * 65, 530);
        ctx.lineTo(660 + i * 65, 170);
        ctx.stroke();
      }
      for (let i = 0; i < 5; i++) {
        ctx.beginPath();
        ctx.moveTo(350, 230 + i * 65);
        ctx.lineTo(950, 230 + i * 65);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
      const sorted = DATA.parts.map((p, i) => ({ p, i })).sort((a, b) => a.p.cy - b.p.cy);
      for (const { p, i } of sorted) {
        const start = 0.25 + (i % 4) * 0.085;
        const assemble = smooth((t - start) / 1.3);
        const explode = 1 - assemble;
        const ang = Math.atan2(p.cy - 78, p.cx - 27);
        const dist = (28 + Math.sqrt(p.area) * 0.85) * explode;
        const dx = Math.cos(ang) * dist;
        const dy = Math.sin(ang) * dist;
        const depth = (12 + (i % 3) * 15) * explode;
        const localTurn = (i % 2 ? 1 : -1) * 0.16 * explode;
        const proj = (v, z = 0) => {
          let x = v[0] - p.cx;
          let y = v[1] - p.cy;
          const xx = x * Math.cos(localTurn) - y * Math.sin(localTurn);
          const yy = x * Math.sin(localTurn) + y * Math.cos(localTurn);
          x = xx + p.cx - 40 + dx;
          y = yy + p.cy - 61 + dy;
          z += depth;
          const x1 = x * Math.cos(tiltY) + z * Math.sin(tiltY);
          const z1 = -x * Math.sin(tiltY) + z * Math.cos(tiltY);
          const y1 = y * Math.cos(tiltX) - z1 * Math.sin(tiltX);
          const z2 = y * Math.sin(tiltX) + z1 * Math.cos(tiltX);
          const perspective = 900 / (900 - z2 * size);
          return [originX + x1 * size * perspective, originY + y1 * size * perspective];
        };
        ctx.globalAlpha = intro;
        // Extruded panel edges: stacked back plates and polished perimeter.
        for (let z = -4.3; z < 0; z += 0.8) {
          path(p.loops, (v) => proj(v, z * (1 - morph)));
          ctx.fillStyle = z < -2 ? "#141b22" : "#72818c";
          ctx.fill("evenodd");
        }
        path(p.loops, (v) => proj(v));
        const metal = ctx.createLinearGradient(originX - 200, 160, originX + 160, 540);
        metal.addColorStop(0, "#e5ebee");
        metal.addColorStop(0.28, "#9faeb7");
        metal.addColorStop(0.46, "#d4dde2");
        metal.addColorStop(0.49, "#acbac2");
        metal.addColorStop(1, "#697d8c");
        ctx.fillStyle = metal;
        ctx.fill("evenodd");
        ctx.strokeStyle = `rgba(237,248,255,${0.58 * (1 - morph)})`;
        ctx.lineWidth = 0.85;
        ctx.stroke();
        if (morph > 0) {
          path(p.loops, (v) => proj(v));
          ctx.fillStyle = `rgba(170,177,184,${morph})`;
          ctx.fill("evenodd");
        }
      }
      // Wordmark uses the supplied letter shapes, not a substitute font.
      ctx.globalAlpha = smooth((t - 2.01) / 0.36);
      letterPath(DATA.word, (v) => [originX + (v[0] - 40) * size, originY + (v[1] - 61) * size]);
      ctx.fillStyle = "#edf0f3";
      ctx.fill("evenodd");
      ctx.globalAlpha = 1;
      const cap = smooth((t - 2.13) / 0.28);
      ctx.globalAlpha = cap * 0.78;
      ctx.fillStyle = "#a7b3bd";
      ctx.font = "11px Arial";
      ctx.textAlign = "center";
      if ("letterSpacing" in ctx) ctx.letterSpacing = "5px";
      ctx.fillText("F A C T O R Y", 640, 506);
      if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";
      ctx.globalAlpha = 1;
    }

    resize();
    const onResize = () => {
      resize();
      render(2.8);
    };
    window.addEventListener("resize", onResize);

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      render(2.8);
      doneRef.current?.();
    } else {
      const started = performance.now();
      const tick = (now) => {
        const t = (now - started) / 1000;
        render(Math.min(t, 2.8));
        if (t < 2.8) frame = requestAnimationFrame(tick);
        else finish();
      };
      frame = requestAnimationFrame(tick);
    }

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(fadeTimer);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <div
      data-testid="factory-intro"
      className={`fixed inset-0 z-[60] bg-[#0c1015] transition-opacity duration-500 ${
        fading ? "pointer-events-none opacity-0" : "opacity-100"
      }`}
    >
      <canvas
        ref={canvasRef}
        className="block h-full w-full"
        aria-label="Okyanus Grup: logo assembly animation"
      />
    </div>
  );
}
